import { HStack } from '@sgme/ui';
import type { MouseEvent } from 'react';
import { Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { v4 as uuid } from 'uuid';

import type { ProductsAccess, ProductType } from '@/api/user/getCurrentUser.ts';
import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { BACKGROUND_LIGHT_COLOR } from '@/App/tiles/CashTile/index.tsx';
import { useUserContext } from '@/context/UserContext.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { algoOrderTypes, limitOrdersTypes } from '@/store/orders/state.ts';
import { selectTileCurrencyPair, selectTileProduct, selectTileProductType } from '@/store/tiles/selectors.ts';
import { updateTileProductType } from '@/store/tiles/slice.ts';

type Props = {
  tileId: string;
};

function ToggleProduct({ tileId }: Props) {
  const track = useTrackEvent();
  const appDispatch = useAppDispatch();
  const {
    currentUser: { productTypes, productsAccess },
  } = useUserContext();

  const authorizedProducts = productTypes.filter((type) => isInstrumentChoiceAuthorized(productsAccess, type));
  const authorizedLimitOrders = limitOrdersTypes.filter((type) => isInstrumentChoiceAuthorized(productsAccess, type));
  const authorizedAlgoOrders = algoOrderTypes.filter((type) => isInstrumentChoiceAuthorized(productsAccess, type));

  const productType = useAppSelector((state) => selectTileProductType(state, tileId));
  const currencyPair = useAppSelector((state) => selectTileCurrencyPair(state, tileId));
  const userPreferences = useAppSelector((state) => state.userPreferences);
  const overriddenClientId = useAppSelector(selectTileProduct(tileId))?.clientId;

  const handleChangeProduct = (productType: ProductType) => (event: MouseEvent<HTMLElement>) => {
    track('Change product', `new product selected : ${productType}`);
    event.stopPropagation();
    appDispatch(
      updateTileProductType({
        tileId,
        productType: productType,
        userPreferences,
        currencyPair,
        overriddenClientId,
        sessionId: uuid(),
      }),
    );
  };

  return (
    <HStack>
      {productType && (
        <Dropdown drop="end">
          <Dropdown.Toggle data-e2e="product-menu" variant="flat-primary">
            <ProductMenuItemLabel short productType={productType} />
          </Dropdown.Toggle>

          {createPortal(
            <Dropdown.Menu>
              {authorizedProducts.map((type) => (
                <Dropdown.Item
                  key={type}
                  onClick={handleChangeProduct(type)}
                  data-e2e={`product-menu-${type.toLowerCase()}`}
                >
                  <ProductMenuItemLabel productType={type} />
                </Dropdown.Item>
              ))}

              {authorizedLimitOrders.length > 0 && (
                <Dropdown color={BACKGROUND_LIGHT_COLOR} drop="end">
                  <Dropdown.Toggle
                    data-e2e="product-limit-orders-menu"
                    variant="flat-primary"
                    className="w-100 justify-content-between"
                  >
                    <FormattedMessage id="productMenu.LimitOrders" />
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    {authorizedLimitOrders.map((type) => (
                      <Dropdown.Item
                        key={type}
                        onClick={handleChangeProduct(type)}
                        data-e2e={`product-menu-${type.toLowerCase()}`}
                      >
                        <ProductMenuItemLabel productType={type} />
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              )}

              {authorizedAlgoOrders.length > 0 && (
                <Dropdown color={BACKGROUND_LIGHT_COLOR} drop="end">
                  <Dropdown.Toggle
                    data-e2e="product-algo-orders-menu"
                    variant="flat-primary"
                    className="w-100 justify-content-between"
                  >
                    <FormattedMessage id="productMenu.AlgoOrders" />
                  </Dropdown.Toggle>

                  <Dropdown.Menu>
                    {authorizedAlgoOrders.map((type) => (
                      <Dropdown.Item
                        key={type}
                        onClick={handleChangeProduct(type)}
                        data-e2e={`product-menu-${type.toLowerCase()}`}
                      >
                        <ProductMenuItemLabel productType={type} />
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              )}
            </Dropdown.Menu>,
            document.body,
          )}
        </Dropdown>
      )}
    </HStack>
  );
}

const ProductMenuItemLabel = ({ productType, short = false }: { productType: ProductType; short?: boolean }) => (
  <FormattedMessage id={`productMenu.${productType}`} values={{ short }} />
);

export default ToggleProduct;

export function isInstrumentChoiceAuthorized(productsAccess: ProductsAccess, instrument: ProductType) {
  switch (instrument) {
    case 'Cash':
      return productsAccess.spot || productsAccess.forward;

    case 'Swap':
      return productsAccess.swap;

    case 'Option':
      return productsAccess.option;

    case 'ForwardAccumulator':
      return productsAccess.forwardAccu;

    case 'TargetAccumulator':
      return productsAccess.targetAccu;

    case 'FlexiForward':
      return productsAccess.americanForward;

    case 'TakeProfitOrder':
      return productsAccess.takeProfit;

    case 'StopLossOrder':
      return productsAccess.stopLoss;

    case 'CallOrder':
      return productsAccess.callOrder;

    case 'FixingOrder':
      return productsAccess.fixing;

    case 'Twap+Order':
      return productsAccess.twap;

    case 'NightjarOrder':
      return productsAccess.nightjar;

    case 'FalconOrder':
      return productsAccess.falcon;

    default:
      return false;
  }
}
